import React, { useMemo, useState, useEffect, useRef } from 'react';
import { X, Clock, SlidersHorizontal, CheckCircle2, Search, UserCheck, Mail, Target, Zap, Building2, MapPin } from 'lucide-react';
import { PlanNudgeBanner } from '../PlanNudgeBanner/PlanNudgeBanner';
import { usePostHog } from 'posthog-js/react';
import { subscriptionsApi, topupsApi } from '../../../api/subscriptions';
import { TopupPack as BalanceTopupPack } from '../../../types/balance.types';
import { DiscoverSummary, BestSearch } from '../../../types/results.types';
import { WEBSITE_MATCH_EXAMPLE } from '../../../constants/websiteMatchExample';

export type UpgradeModalContext = 'generic' | 'results_lock' | 'locked_page' | 'search_limit' | 'dm_unlock_exhausted' | 'discover_exhausted' | 'smart_filters';
type UpgradePlanName = 'starter' | 'growth' | 'scale';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  creditsNeeded?: number;
  currentPlan?: string;
  lockedPlacesCount?: number;
  isLockedPageAccess?: boolean;
  title?: string;
  subtitle?: string;
  context?: UpgradeModalContext;
  recommendedPlanName?: UpgradePlanName;
  qualifyingPlanNames?: UpgradePlanName[];
  plansOnly?: boolean;
  searchLimitInfo?: {
    activeSearches: number;
    maxAllowed: number;
  };
  visibleResultsCap?: number | null;
  searchId?: number;
  dmUnlocksUsed?: number | null;
  allTimeDecisionMakerEmailsFound?: number | null;
  discoverSummary?: DiscoverSummary;
  bestSearch?: BestSearch | null;
  autoFindMode?: string | null;
  autoFindSuccessLimit?: number;
  autoFindSuccessCount?: number;
  autoFindAttemptCount?: number;
  autoFindNoEmailCount?: number;
  autoFindCatchAllCount?: number;
  autoFindGuardrailReason?: string | null;
}

interface Plan {
  id: number;
  name: string;
  display_name: string;
  daily_searches: number;
  max_results?: number | null;
  price_cents: number;
  monthly_dm_unlocks: number;
  monthly_email_verifications: number;
  price_display: string;
  badge_type: 'MOST_POPULAR' | 'BEST_VALUE' | 'USAGE_BASED' | null;
  features: {
    allow_multiple_queued_searches: boolean;
    max_queued_searches: number;
    allow_topups: boolean;
    priority_support: boolean;
    dedicated_support: boolean;
    email_discovery: boolean;
    email_verification: boolean;
    confidence_badges: boolean;
  };
}

interface ModalFeatureItem {
  title: string;
}

interface ModalExampleContent {
  label: string;
  title: string;
  body: string;
}

interface WebsiteMatchVariantContent {
  badge: string;
  title: string;
  subtitle: string;
  example: ModalExampleContent;
  featureItems: ModalFeatureItem[];
  rightTitle: string;
  includedLabel: string;
  includedFeatureLabel: string;
  ctaPrefix: string;
  dismissLabel: string;
}

const PLAN_ORDER: Record<string, number> = {
  starter: 1,
  growth: 2,
  scale: 3,
};

const WEBSITE_MATCH_VARIANT: WebsiteMatchVariantContent = {
  badge: 'Website Match',
  title: 'Find better-fit businesses before you export',
  subtitle:
    "Website Match checks each company website for the services, keywords, and signals you care about. Included on Starter and above.",
  example: {
    label: 'Example',
    title: 'HVAC contractors that offer emergency service',
    body:
      'Search HVAC contractors, then keep only sites that mention emergency repair, 24/7 service, or same-day help.',
  },
  featureItems: [
    {
      title: 'Build lists around the services your client wants to grow.',
    },
    {
      title: 'Give outreach a more relevant reason to reach out.',
    },
  ],
  rightTitle: 'Upgrade to unlock',
  includedLabel: 'Includes Website Match on every search.',
  includedFeatureLabel: 'Website Match for every search',
  ctaPrefix: 'Upgrade to',
  dismissLabel: 'Not now',
};

const formatActiveSearches = (maxSearches: number): string => {
  if (maxSearches <= 1) return '1 active search';
  return `Up to ${maxSearches} active searches`;
};

const getSearchLimitHeadline = (maxAllowed: number): string => {
  return `Your current plan supports ${maxAllowed} active search${maxAllowed === 1 ? '' : 'es'}`;
};

const formatSearchesPerDay = (count: number): string => {
  return `${count.toLocaleString()} ${count === 1 ? 'search' : 'searches'} / day`;
};

const renderRedirectingOverlay = () => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-white bg-opacity-95">
    <div className="text-center">
      <div className="mb-4 inline-block animate-bounce text-6xl">🚀</div>
      <h3 className="mb-2 text-xl font-semibold text-gray-900">Redirecting to checkout...</h3>
      <p className="text-gray-600">Please wait while we prepare your payment</p>
    </div>
  </div>
);

export const UpgradeModal: React.FC<UpgradeModalProps> = ({
  isOpen,
  onClose,
  creditsNeeded = 0,
  currentPlan = 'discover',
  lockedPlacesCount = 0,
  isLockedPageAccess = false,
  title,
  subtitle,
  context = 'generic',
  recommendedPlanName,
  qualifyingPlanNames,
  plansOnly = false,
  searchLimitInfo,
  visibleResultsCap = null,
  searchId,
  dmUnlocksUsed = null,
  allTimeDecisionMakerEmailsFound = null,
  discoverSummary,
  bestSearch = null,
  autoFindMode = null,
  autoFindSuccessLimit = 0,
  autoFindSuccessCount = 0,
  autoFindAttemptCount = 0,
  autoFindNoEmailCount = 0,
  autoFindCatchAllCount = 0,
  autoFindGuardrailReason = null,
}) => {
  const normalizedCurrentPlan = currentPlan || 'discover';
  const posthog = usePostHog();

  const canShowTopups =
    !plansOnly && context !== 'search_limit' && context !== 'results_lock' && context !== 'locked_page';
  const defaultTab: 'plans' | 'topups' =
    canShowTopups && normalizedCurrentPlan === 'scale' ? 'topups' : 'plans';

  const [activeTab, setActiveTab] = useState<'topups' | 'plans'>(defaultTab);
  const [topupPacks, setTopupPacks] = useState<BalanceTopupPack[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const searchLimitModalViewedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!isOpen) return;
    setActiveTab(defaultTab);
    setShowAllDmPlans(false);
    setShowAllDiscoverPlans(false);
    setShowAllSmartFilterPlans(false);
    void loadOptions();
  }, [isOpen, defaultTab]);

  const loadOptions = async () => {
    setLoading(true);
    try {
      if (canShowTopups) {
        const [topupsResponse, plansResponse] = await Promise.all([
          topupsApi.getPacks(),
          subscriptionsApi.getPlans(),
        ]);
        setTopupPacks(topupsResponse.packs || []);
        setPlans((plansResponse.plans || []) as Plan[]);
      } else {
        const plansResponse = await subscriptionsApi.getPlans();
        setTopupPacks([]);
        setPlans((plansResponse.plans || []) as Plan[]);
      }
    } catch (error) {
      console.error('Failed to load upgrade options:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPlans = useMemo(() => {
    let nextPlans = [...plans].filter((plan) => plan.name !== normalizedCurrentPlan && plan.name !== 'free');

    if (normalizedCurrentPlan !== 'discover') {
      nextPlans = nextPlans.filter((plan) => plan.name !== 'discover');
    }

    if (context === 'search_limit' && normalizedCurrentPlan !== 'discover') {
      if (qualifyingPlanNames && qualifyingPlanNames.length > 0) {
        nextPlans = nextPlans.filter((plan) => qualifyingPlanNames.includes(plan.name as UpgradePlanName));
      } else if (searchLimitInfo) {
        nextPlans = nextPlans.filter((plan) => (plan.features?.max_queued_searches || 0) > searchLimitInfo.maxAllowed);
      }
    }

    return nextPlans.sort((a, b) => {
      const byPrice = (a.price_cents || 0) - (b.price_cents || 0);
      if (byPrice !== 0) return byPrice;
      return (PLAN_ORDER[a.name] || 999) - (PLAN_ORDER[b.name] || 999);
    });
  }, [plans, normalizedCurrentPlan, context, qualifyingPlanNames, searchLimitInfo]);

  const recommendedPlan = useMemo(() => {
    if (recommendedPlanName) {
      const explicit = filteredPlans.find((plan) => plan.name === recommendedPlanName);
      if (explicit) return explicit;
    }
    return filteredPlans[0];
  }, [filteredPlans, recommendedPlanName]);

  const computedTitle = useMemo(() => {
    if (title) return title;
    if (context === 'smart_filters') {
      return 'Find better-fit local prospects before you export';
    }
    if (context === 'search_limit' && searchLimitInfo) {
      if (normalizedCurrentPlan === 'discover') {
        return 'Unlock more searches';
      }
      return getSearchLimitHeadline(searchLimitInfo.maxAllowed);
    }
    if (context === 'results_lock') {
      return `You found ${lockedPlacesCount.toLocaleString()} businesses`;
    }
    if (context === 'locked_page') {
      return 'These results are locked';
    }
    if (context === 'dm_unlock_exhausted') {
      return "You've used all your decision-maker email credits";
    }
    return 'Choose a plan to continue';
  }, [title, context, searchLimitInfo, lockedPlacesCount, normalizedCurrentPlan]);

  const computedSubtitle = useMemo(() => {
    if (subtitle) return subtitle;

    if (context === 'smart_filters') {
      return "Smart filters check each company's website for the services your agency wants to target. Included on every paid plan.";
    }

    if (context === 'search_limit' && searchLimitInfo) {
      if (normalizedCurrentPlan === 'discover') {
        return 'Starter and Growth unlock more daily searches and results. Scale is the only plan that lets you run 2 searches at the same time.';
      }
      if (!recommendedPlan) {
        return `You already have ${searchLimitInfo.activeSearches} running. Close one active search to start another.`;
      }
      const recommendedLimit = recommendedPlan.features?.max_queued_searches || searchLimitInfo.maxAllowed;
      return `You already have ${searchLimitInfo.activeSearches} running. Upgrade to ${recommendedPlan.display_name} to run up to ${recommendedLimit} at once.`;
    }

    if (context === 'results_lock') {
      if (visibleResultsCap !== null) {
        return `Your free plan shows ${visibleResultsCap.toLocaleString()} results per search. Paid plans unlock the full list, starting at $24/mo.`;
      }
      return 'Your free plan shows a limited slice of each search. Paid plans unlock the full list, starting at $24/mo.';
    }

    if (context === 'locked_page') {
      return 'Upgrade your plan to access this page of results.';
    }

    if (context === 'dm_unlock_exhausted') {
      if (dmUnlocksUsed) {
        return `You've unlocked ${dmUnlocksUsed.toLocaleString()} decision-maker emails on ${normalizedCurrentPlan === 'discover' ? 'Discover' : 'your plan'}. Upgrade to keep finding owner and decision-maker emails.`;
      }
      return 'Upgrade to keep finding owner and decision-maker emails.';
    }

    if (creditsNeeded > 0) {
      return 'Upgrade your plan to get more searches per day, people enrichments, and email verifications.';
    }

    return 'Pick the best plan for your current workflow.';
  }, [subtitle, context, searchLimitInfo, recommendedPlan, visibleResultsCap, creditsNeeded, dmUnlocksUsed, normalizedCurrentPlan]);

  const shouldShowPlansOnly = context === 'search_limit' || plansOnly;
  const shouldRenderTopups = !shouldShowPlansOnly && canShowTopups && activeTab === 'topups';

  useEffect(() => {
    if (!isOpen || context !== 'search_limit') return;
    if (loading) return;

    const impressionKey = [
      normalizedCurrentPlan,
      searchLimitInfo?.activeSearches ?? 'na',
      searchLimitInfo?.maxAllowed ?? 'na',
      recommendedPlan?.name ?? 'none',
      filteredPlans.map((plan) => plan.name).join(','),
    ].join('|');

    if (searchLimitModalViewedRef.current.has(impressionKey)) return;
    searchLimitModalViewedRef.current.add(impressionKey);

    posthog?.capture('search_limit_modal_opened', {
      context: 'search_limit',
      current_plan: normalizedCurrentPlan,
      daily_searches: null,
      max_results: null,
      max_queued_searches: searchLimitInfo?.maxAllowed ?? null,
      recommended_plan: recommendedPlan?.name ?? null,
      recommended_limit: recommendedPlan?.features?.max_queued_searches ?? null,
      active_searches: searchLimitInfo?.activeSearches ?? null,
      locked_places_count: null,
      search_id: searchId ?? null,
    });
  }, [
    isOpen,
    context,
    loading,
    normalizedCurrentPlan,
    filteredPlans,
    recommendedPlan,
    searchId,
    searchLimitInfo,
    posthog,
  ]);

  const handleTopupPurchase = async (packId: number) => {
    try {
      setRedirecting(true);
      const response = await topupsApi.purchaseTopup(
        packId,
        `${window.location.origin}/topup/success`,
        `${window.location.origin}/topup/cancel`
      );
      window.location.href = response.checkout_url;
    } catch (error) {
      console.error('Failed to create top-up checkout:', error);
      setRedirecting(false);
    }
  };

  const handlePlanUpgrade = async (plan: Plan) => {
    if (context === 'smart_filters') {
      posthog?.capture('smart_filters_upgrade_clicked', {
        current_plan: normalizedCurrentPlan,
        selected_plan: plan.name,
        business_type: null,
        source: 'search_create_toggle',
      });
    }

    posthog?.capture('upgrade_plan_selected', {
      context,
      current_plan: normalizedCurrentPlan,
      selected_plan: plan.name,
      recommended_plan: recommendedPlan?.name || null,
      recommended_limit: recommendedPlan?.features?.max_queued_searches ?? null,
      daily_searches: plan.daily_searches,
      max_results: plan.max_results ?? null,
      max_queued_searches: plan.features?.max_queued_searches ?? null,
      active_searches: searchLimitInfo?.activeSearches ?? null,
      locked_places_count: lockedPlacesCount || null,
      search_id: searchId ?? null,
      auto_find_mode: autoFindMode,
      success_limit: autoFindSuccessLimit,
      success_count: autoFindSuccessCount,
      attempt_count: autoFindAttemptCount,
      no_email_count: autoFindNoEmailCount,
      catch_all_count: autoFindCatchAllCount,
      gate_reason: autoFindGuardrailReason,
      purchased_dm_spent: 0,
    });

    try {
      setRedirecting(true);
      const response = await subscriptionsApi.createCheckoutSession(
        plan.id,
        `${window.location.origin}/subscription/success`,
        `${window.location.origin}/subscription/cancel`
      );
      window.location.href = response.checkout_url;
    } catch (error) {
      console.error('Failed to create plan checkout:', error);
      setRedirecting(false);
    }
  };

  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [showAllDmPlans, setShowAllDmPlans] = useState(false);
  const [showAllDiscoverPlans, setShowAllDiscoverPlans] = useState(false);
  const [showAllSmartFilterPlans, setShowAllSmartFilterPlans] = useState(false);

  const selectedPlan = useMemo(() => {
    if (selectedPlanId !== null) {
      return filteredPlans.find((p) => p.id === selectedPlanId) ?? recommendedPlan;
    }
    return recommendedPlan;
  }, [selectedPlanId, filteredPlans, recommendedPlan]);

  const activeVariant = useMemo(() => {
    if (context === 'smart_filters') {
      return WEBSITE_MATCH_VARIANT;
    }
    return null;
  }, [context]);

  if (!isOpen) return null;

  if (context === 'discover_exhausted') {
    const selectedDiscoverPlan = selectedPlan ?? recommendedPlan ?? filteredPlans.find((p) => p.name === 'starter') ?? filteredPlans[0];

    if (showAllDiscoverPlans) {
      return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
              <button
                onClick={() => setShowAllDiscoverPlans(false)}
                className="text-sm font-medium text-[#8A8F9E] hover:text-[#5C6478] transition-colors"
              >
                ← Back
              </button>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors" aria-label="Close modal">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6">
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {filteredPlans.map((plan) => {
                    const isRecommended = recommendedPlan?.id === plan.id;
                    const maxSearches = plan.features?.max_queued_searches || 1;
                    return (
                      <div
                        key={plan.id}
                        className={`relative rounded-xl transition-all flex flex-col h-full ${
                          isRecommended
                            ? 'border-2 border-primary shadow-xl ring-2 ring-primary/20'
                            : 'border-2 border-gray-200 hover:border-primary hover:shadow-lg'
                        }`}
                      >
                        <div className="relative bg-white rounded-[8px] p-6 flex flex-col h-full">
                          <div className="mb-3 flex flex-col gap-2">
                            {isRecommended && (
                              <div className="inline-flex items-center bg-primary text-white text-xs font-semibold px-3 py-1 rounded-full self-start">
                                Recommended
                              </div>
                            )}
                          </div>
                          <div className="flex-1 flex flex-col">
                            <h3 className="text-2xl font-bold text-primary mb-3">{plan.display_name}</h3>
                            <div className="flex items-baseline gap-2 mb-3">
                              <span className="text-4xl font-bold text-secondary">{plan.price_display}</span>
                              <span className="text-gray-600 text-lg">per month</span>
                            </div>
                            <div className="space-y-2 mb-5">
                              <p className="text-sm text-gray-700 font-semibold">
                                {plan.daily_searches} {plan.daily_searches === 1 ? 'search' : 'searches'} / day
                              </p>
                              {plan.max_results != null && (
                                <p className="text-sm text-gray-700 font-semibold">
                                  Up to {plan.max_results.toLocaleString()} results / search
                                </p>
                              )}
                              <p className="text-sm text-gray-700">{formatActiveSearches(maxSearches)}</p>
                              <p className="text-sm text-gray-700">
                                {plan.monthly_dm_unlocks.toLocaleString()} people enrichments / month
                              </p>
                              <p className="text-sm text-gray-700">
                                {plan.monthly_email_verifications.toLocaleString()} verifications / month
                              </p>
                            </div>
                            <button
                              onClick={() => void handlePlanUpgrade(plan)}
                              className={`mt-auto py-4 px-8 rounded-lg font-bold text-lg transition-all whitespace-nowrap shadow-md hover:shadow-xl ${
                                isRecommended
                                  ? 'bg-primary hover:bg-primary-hover text-white'
                                  : 'bg-secondary hover:bg-secondary-hover text-white'
                              }`}
                            >
                              Upgrade Now
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          {redirecting && renderRedirectingOverlay()}
        </div>
      );
    }

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
        <div className="relative max-h-[90vh] w-full max-w-4xl overflow-y-auto overflow-x-hidden rounded-2xl bg-white shadow-xl">
          <button
            onClick={onClose}
            className="absolute right-6 top-6 z-20 text-[#8A8F9E] transition-colors hover:text-[#5C6478]"
            aria-label="Close modal"
          >
            <X className="h-6 w-6" />
          </button>

          <div className="grid md:grid-cols-[1.05fr_0.95fr]">
            {/* Left panel */}
            <div className="bg-[radial-gradient(circle_at_top_left,_rgba(216,178,110,0.18),_transparent_45%),linear-gradient(180deg,_#FBF7F0_0%,_#F8F2EA_100%)] px-6 py-7 sm:px-8">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#E5D2B5] bg-white px-4 py-2 shadow-sm">
                <Search className="h-3.5 w-3.5 text-[#B3261E]" />
                <span className="text-xs font-semibold uppercase tracking-[0.12em] text-[#B3261E]">Searches Used</span>
              </div>

              <h2 className="max-w-xl text-3xl font-bold leading-tight text-[#14213D] sm:text-[36px]">
                You've used all your searches.
              </h2>

              {/* Already found stats */}
              {discoverSummary && (
                <div className="mt-5">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-[0.1em] text-[#8A8F9E]">You've already found:</p>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { icon: <Building2 className="h-4 w-4 text-blue-500" />, value: discoverSummary.businesses.toLocaleString(), label: 'Businesses' },
                      { icon: <UserCheck className="h-4 w-4 text-purple-500" />, value: discoverSummary.decision_makers.toLocaleString(), label: 'Decision-makers' },
                      { icon: <Mail className="h-4 w-4 text-green-500" />, value: discoverSummary.emails.toLocaleString(), label: 'Emails found' },
                    ].map((stat) => (
                      <div key={stat.label} className="flex flex-col items-center gap-1 rounded-xl border border-[#E7D7C3] bg-white px-3 py-3 text-center">
                        {stat.icon}
                        <p className="text-base font-bold text-[#14213D]">{stat.value}</p>
                        <p className="text-xs text-[#8A8F9E]">{stat.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Best search example */}
              {bestSearch && (
                <div className="mt-5 rounded-2xl border border-[#E7D7C3] bg-white px-5 py-4 shadow-sm">
                  <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#B3261E]">Example next list</p>
                  <div className="mt-2 flex items-center gap-2">
                    <MapPin className="h-4 w-4 shrink-0 text-[#B3261E]" />
                    <div>
                      <p className="text-sm font-bold text-[#14213D]">{bestSearch.search_query}</p>
                      <p className="text-xs text-[#8A8F9E]">{[bestSearch.city_name, bestSearch.state_name].filter(Boolean).join(', ')}</p>
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    {[
                      { icon: <Building2 className="h-3.5 w-3.5 text-blue-500" />, value: bestSearch.businesses.toLocaleString(), label: 'Businesses' },
                      { icon: <UserCheck className="h-3.5 w-3.5 text-purple-500" />, value: bestSearch.decision_makers.toLocaleString(), label: 'Decision-makers' },
                      { icon: <Mail className="h-3.5 w-3.5 text-green-500" />, label: 'Emails ready to find' },
                    ].map((stat) => (
                      <div key={stat.label} className="flex flex-col items-center gap-1 rounded-xl border border-[#E7D7C3] bg-[#FAFAF9] px-2 py-2 text-center">
                        {stat.icon}
                        <p className="text-sm font-bold text-[#14213D]">{stat.value}</p>
                        <p className="text-xs text-[#8A8F9E]">{stat.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Feature bullets */}
              <div className="mt-6 divide-y divide-[#E9DED0]">
                {[
                  { icon: <Target className="h-4 w-4 text-[#B3261E]" />, text: 'Test new cities and niches every day' },
                  { icon: <CheckCircle2 className="h-4 w-4 text-[#B3261E]" />, text: 'Find and verify decision-maker emails before export' },
                ].map((item) => (
                  <div key={item.text} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#E5D2B5] bg-white">
                      {item.icon}
                    </div>
                    <p className="text-sm leading-6 text-[#24324D]">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right panel */}
            <div className="flex flex-col justify-center bg-white px-6 py-7 pr-14 sm:px-8 sm:pr-14">
              <p className="text-sm font-semibold uppercase tracking-[0.12em] text-[#B3261E]">Upgrade to keep going</p>

              {loading ? (
                <div className="flex flex-1 items-center justify-center py-10">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                </div>
              ) : selectedDiscoverPlan ? (
                <>
                  <div className="mt-5 rounded-2xl border-2 border-[#D8A43C] bg-[#FFFEFB] p-5 shadow-sm">
                    <div className="inline-flex rounded-full bg-[#D8A43C] px-4 py-1.5 text-xs font-semibold text-white">
                      Recommended
                    </div>
                    <div className="mt-4">
                      <h3 className="text-3xl font-bold leading-none text-[#14213D]">{selectedDiscoverPlan.display_name}</h3>
                      <p className="mt-1.5 text-sm text-[#5C6478]">Best for building targeted local lead lists.</p>
                      <div className="mt-4 border-t border-[#E9DED0] pt-4">
                        <span className="text-4xl font-bold text-[#14213D]">{selectedDiscoverPlan.price_display}</span>
                        <span className="text-lg text-[#7A8191]">/mo</span>
                      </div>
                    </div>

                    <div className="mt-5 space-y-4 border-t border-[#E9DED0] pt-5">
                      <div className="flex items-start gap-3">
                        <Search className="mt-0.5 h-5 w-5 shrink-0 text-indigo-500" />
                        <div>
                          <p className="text-sm font-semibold text-[#14213D]">
                            {selectedDiscoverPlan.daily_searches} fresh {selectedDiscoverPlan.daily_searches === 1 ? 'search' : 'searches'} / day
                          </p>
                          <p className="text-xs text-[#8A8F9E]">Build a new niche + city list every day</p>
                        </div>
                      </div>
                      {selectedDiscoverPlan.max_results != null && (
                        <div className="flex items-start gap-3">
                          <Zap className="mt-0.5 h-5 w-5 shrink-0 text-[#D8A43C]" />
                          <div>
                            <p className="text-sm font-semibold text-[#14213D]">
                              {selectedDiscoverPlan.max_results.toLocaleString()} results / search
                            </p>
                            <p className="text-xs text-[#8A8F9E]">More businesses to qualify and pitch</p>
                          </div>
                        </div>
                      )}
                      <div className="flex items-start gap-3">
                        <UserCheck className="mt-0.5 h-5 w-5 shrink-0 text-purple-500" />
                        <div>
                          <p className="text-sm font-semibold text-[#14213D]">
                            {selectedDiscoverPlan.monthly_dm_unlocks.toLocaleString()} decision-maker email lookups / month
                          </p>
                          <p className="text-xs text-[#8A8F9E]">Find emails for the people you want to contact</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <Mail className="mt-0.5 h-5 w-5 shrink-0 text-green-500" />
                        <div>
                          <p className="text-sm font-semibold text-[#14213D]">
                            {selectedDiscoverPlan.monthly_email_verifications.toLocaleString()} email verifications / month
                          </p>
                          <p className="text-xs text-[#8A8F9E]">Clean your list before export</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => void handlePlanUpgrade(selectedDiscoverPlan)}
                    disabled={redirecting}
                    className="mt-6 w-full rounded-xl bg-[#121C33] px-6 py-4 text-base font-semibold text-white transition-colors hover:bg-[#0B1427] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {redirecting ? 'Redirecting...' : 'Build new lead list today →'}
                  </button>
                </>
              ) : (
                <div className="flex flex-1 items-center justify-center py-10">
                  <p className="text-sm text-gray-500">No paid plans are available right now.</p>
                </div>
              )}

              <button
                onClick={() => setShowAllDiscoverPlans(true)}
                className="mt-4 text-center text-sm font-medium text-[#14213D] underline transition-colors hover:text-[#5C6478]"
              >
                Compare all plans
              </button>
              <button
                onClick={onClose}
                className="mt-3 text-center text-sm font-medium text-[#8A8F9E] transition-colors hover:text-[#5C6478]"
              >
                Not now
              </button>
            </div>
          </div>
        </div>
        {redirecting && renderRedirectingOverlay()}
      </div>
    );
  }

  if (context === 'dm_unlock_exhausted') {
    const foundCount = allTimeDecisionMakerEmailsFound ?? 0;
    const isExhaustedLoading = loading || allTimeDecisionMakerEmailsFound === null;
    const selectedDmPlan = selectedPlan ?? recommendedPlan ?? filteredPlans.find((p) => p.name === 'starter') ?? filteredPlans[0];

    if (showAllDmPlans) {
      return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
              <button
                onClick={() => setShowAllDmPlans(false)}
                className="text-sm font-medium text-[#8A8F9E] hover:text-[#5C6478] transition-colors"
              >
                ← Back
              </button>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors" aria-label="Close modal">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6">
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {filteredPlans.map((plan) => {
                    const isRecommended = recommendedPlan?.id === plan.id;
                    const maxSearches = plan.features?.max_queued_searches || 1;
                    return (
                      <div
                        key={plan.id}
                        className={`relative rounded-xl transition-all flex flex-col h-full ${
                          isRecommended
                            ? 'border-2 border-primary shadow-xl ring-2 ring-primary/20'
                            : 'border-2 border-gray-200 hover:border-primary hover:shadow-lg'
                        }`}
                      >
                        <div className="relative bg-white rounded-[8px] p-6 flex flex-col h-full">
                          <div className="mb-3 flex flex-col gap-2">
                            {isRecommended && (
                              <div className="inline-flex items-center bg-primary text-white text-xs font-semibold px-3 py-1 rounded-full self-start">
                                Recommended
                              </div>
                            )}
                          </div>
                          <div className="flex-1 flex flex-col">
                            <h3 className="text-2xl font-bold text-primary mb-3">{plan.display_name}</h3>
                            <div className="flex items-baseline gap-2 mb-3">
                              <span className="text-4xl font-bold text-secondary">{plan.price_display}</span>
                              <span className="text-gray-600 text-lg">per month</span>
                            </div>
                            <div className="space-y-2 mb-5">
                              <p className="text-sm text-gray-700 font-semibold">
                                {plan.daily_searches} {plan.daily_searches === 1 ? 'search' : 'searches'} / day
                              </p>
                              {plan.max_results != null && (
                                <p className="text-sm text-gray-700 font-semibold">
                                  Up to {plan.max_results.toLocaleString()} results / search
                                </p>
                              )}
                              <p className="text-sm text-gray-700">{formatActiveSearches(maxSearches)}</p>
                              <p className="text-sm text-gray-700">
                                {plan.monthly_dm_unlocks.toLocaleString()} people enrichments / month
                              </p>
                              <p className="text-sm text-gray-700">
                                {plan.monthly_email_verifications.toLocaleString()} verifications / month
                              </p>
                            </div>
                            <button
                              onClick={() => handlePlanUpgrade(plan)}
                              className={`mt-auto py-4 px-8 rounded-lg font-bold text-lg transition-all whitespace-nowrap shadow-md hover:shadow-xl ${
                                isRecommended
                                  ? 'bg-primary hover:bg-primary-hover text-white'
                                  : 'bg-secondary hover:bg-secondary-hover text-white'
                              }`}
                            >
                              Upgrade Now
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          {redirecting && renderRedirectingOverlay()}
        </div>
      );
    }

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
        <div className="relative max-h-[90vh] w-full max-w-4xl overflow-y-auto overflow-x-hidden rounded-2xl bg-white shadow-xl">
          <button
            onClick={onClose}
            className="absolute right-6 top-6 z-20 text-[#8A8F9E] transition-colors hover:text-[#5C6478]"
            aria-label="Close modal"
          >
            <X className="h-6 w-6" />
          </button>

          <div className="grid md:grid-cols-[1.05fr_0.95fr]">
            {/* Left panel */}
            <div className="bg-[radial-gradient(circle_at_top_left,_rgba(216,178,110,0.18),_transparent_45%),linear-gradient(180deg,_#FBF7F0_0%,_#F8F2EA_100%)] px-6 py-7 sm:px-8">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#E5D2B5] bg-white px-4 py-2 shadow-sm">
                <UserCheck className="h-3.5 w-3.5 text-[#B3261E]" />
                <span className="text-xs font-semibold uppercase tracking-[0.12em] text-[#B3261E]">Decision-Maker Emails</span>
              </div>

              <h2 className="max-w-xl text-3xl font-bold leading-tight text-[#14213D] sm:text-[36px]">
                Find more decision-maker emails for your lead lists.
              </h2>

              {!isExhaustedLoading && selectedDmPlan && (
                <p className="mt-4 text-sm leading-relaxed text-[#5C6478]">
                  You've already found <strong className="text-[#14213D]">{foundCount.toLocaleString()}</strong> decision-makers.{' '}
                  {selectedDmPlan.display_name} adds {selectedDmPlan.monthly_dm_unlocks.toLocaleString()} monthly people enrichments so you can keep finding decision-maker emails for the best-fit businesses you want to pitch.
                </p>
              )}

              <div className="mt-6 rounded-2xl border border-[#E7D7C3] bg-white px-5 py-5 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#B3261E]">Example</p>
                <div className="mt-3 flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#F0EBE3] text-xs font-bold text-[#5C3D2E]">RW</div>
                  <div>
                    <p className="text-sm font-bold text-[#14213D]">Robert Wallace</p>
                    <p className="text-xs text-[#8A8F9E]">Owner</p>
                  </div>
                </div>
                <div className="mt-4 grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] sm:items-stretch">
                  <div>
                    <p className="mb-2 min-h-[32px] text-center text-xs font-medium leading-4 text-[#8A8F9E]">Decision-maker found</p>
                    <div className="flex min-h-[98px] flex-col items-center justify-center rounded-xl border border-[#E7D7C3] bg-[#FAFAF9] px-3 py-3 text-center">
                      <UserCheck className="h-5 w-5 text-[#8A8F9E]" />
                      <p className="mt-2 text-xs font-medium text-[#5C6478]">Robert Wallace</p>
                      <p className="text-xs text-[#8A8F9E]">Owner</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-center gap-2 sm:w-14 sm:flex-col sm:pt-10">
                    <p className="text-center text-xs font-medium leading-4 text-[#8A8F9E] sm:sr-only">Use 1 people enrichment</p>
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#E5D2B5] bg-white">
                      <span className="text-sm text-[#8A8F9E]">→</span>
                    </div>
                    <p className="hidden text-center text-[11px] font-medium leading-4 text-[#8A8F9E] sm:block">1 people enrichment</p>
                  </div>
                  <div>
                    <p className="mb-2 min-h-[32px] text-center text-xs font-medium leading-4 text-[#8A8F9E]">Decision-maker email found</p>
                    <div className="flex min-h-[98px] min-w-0 flex-col items-center justify-center rounded-xl border border-[#C8E6C9] bg-[#F1F8F1] px-3 py-3 text-center">
                      <Mail className="h-5 w-5 text-[#4CAF50]" />
                      <p className="mt-2 w-full truncate text-xs font-medium text-[#14213D]" title="r.wallace@company.com">
                        r.wallace@company.com
                      </p>
                      <p className="text-xs text-[#4CAF50]">Ready to verify</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 divide-y divide-[#E9DED0]">
                {[
                  { icon: <UserCheck className="h-4 w-4 text-[#B3261E]" />, text: 'Find emails for owners, founders, and managers' },
                  { icon: <SlidersHorizontal className="h-4 w-4 text-[#B3261E]" />, text: 'Build cleaner lists before export and outreach' },
                ].map((item) => (
                  <div key={item.text} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#E5D2B5] bg-white">
                      {item.icon}
                    </div>
                    <p className="text-sm leading-6 text-[#24324D]">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right panel */}
            <div className="flex flex-col justify-center bg-white px-6 py-7 pr-14 sm:px-8 sm:pr-14">
              <p className="text-sm font-semibold uppercase tracking-[0.12em] text-[#B3261E]">Upgrade to unlock</p>

              {isExhaustedLoading ? (
                <div className="flex flex-1 items-center justify-center py-10">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                </div>
              ) : selectedDmPlan ? (
                <>
                  <div className="mt-5 rounded-2xl border-2 border-[#D8A43C] bg-[#FFFEFB] p-5 shadow-sm">
                    <div className="inline-flex rounded-full bg-[#D8A43C] px-4 py-1.5 text-xs font-semibold text-white">
                      Recommended
                    </div>
                    <div className="mt-4">
                      <h3 className="text-3xl font-bold leading-none text-[#14213D]">{selectedDmPlan.display_name}</h3>
                      <p className="mt-1.5 text-sm text-[#5C6478]">Best for building targeted local lead lists.</p>
                      <div className="mt-4 border-t border-[#E9DED0] pt-4">
                        <span className="text-4xl font-bold text-[#14213D]">{selectedDmPlan.price_display}</span>
                        <span className="text-lg text-[#7A8191]">/mo</span>
                      </div>
                    </div>

                    <div className="mt-5 space-y-4 border-t border-[#E9DED0] pt-5">
                      <div className="flex items-start gap-3">
                        <UserCheck className="mt-0.5 h-5 w-5 shrink-0 text-purple-500" />
                        <div>
                          <p className="text-sm font-semibold text-[#14213D]">
                            {selectedDmPlan.monthly_dm_unlocks.toLocaleString()} decision-maker email lookups / month
                          </p>
                          <p className="text-xs text-[#8A8F9E]">People enrichments for decision-makers you found</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <Mail className="mt-0.5 h-5 w-5 shrink-0 text-green-500" />
                        <div>
                          <p className="text-sm font-semibold text-[#14213D]">
                            {selectedDmPlan.monthly_email_verifications.toLocaleString()} email verifications / month
                          </p>
                          <p className="text-xs text-[#8A8F9E]">Clean contacts before export</p>
                        </div>
                      </div>
                      {selectedDmPlan.max_results != null && (
                        <div className="flex items-start gap-3">
                          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#D8A43C]" />
                          <div>
                            <p className="text-sm font-semibold text-[#14213D]">
                              {selectedDmPlan.max_results.toLocaleString()} results / search
                            </p>
                            <p className="text-xs text-[#8A8F9E]">Build larger niche + city lists</p>
                          </div>
                        </div>
                      )}
                      <div className="flex items-start gap-3">
                        <Search className="mt-0.5 h-5 w-5 shrink-0 text-indigo-500" />
                        <div>
                          <p className="text-sm font-semibold text-[#14213D]">
                            {selectedDmPlan.daily_searches} search / day
                          </p>
                          <p className="text-xs text-[#8A8F9E]">Keep creating new lead lists</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => void handlePlanUpgrade(selectedDmPlan)}
                    disabled={redirecting}
                    className="mt-6 w-full rounded-xl bg-[#121C33] px-6 py-4 text-base font-bold text-white shadow-md transition-colors hover:bg-[#0B1427] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {redirecting
                      ? 'Redirecting...'
                      : 'Find more decision maker emails'}
                  </button>
                </>
              ) : (
                <div className="flex flex-1 items-center justify-center py-10">
                  <p className="text-sm text-gray-500">No paid plans are available right now.</p>
                </div>
              )}

              <button
                onClick={() => setShowAllDmPlans(true)}
                className="mt-4 text-center text-sm font-medium text-[#14213D] underline transition-colors hover:text-[#5C6478]"
              >
                Compare all plans
              </button>
              <button
                onClick={onClose}
                className="mt-3 text-center text-sm font-medium text-[#8A8F9E] transition-colors hover:text-[#5C6478]"
              >
                Not now
              </button>
            </div>
          </div>
        </div>
        {redirecting && renderRedirectingOverlay()}
      </div>
    );
  }

  if (context === 'search_limit') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl w-full shadow-xl overflow-hidden" style={{ maxWidth: 680 }}>
          <div className="grid grid-cols-[220px_1fr]">

            {/* Left: dark context panel */}
            <div className="bg-secondary flex flex-col gap-5 p-6 relative overflow-hidden">
              <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-accent/10" style={{ filter: 'blur(32px)' }} />
              <div className="absolute -bottom-10 -left-6 w-40 h-40 rounded-full bg-primary/15" style={{ filter: 'blur(40px)' }} />
              <div className="relative z-10 flex flex-col gap-4 h-full">
                <div className="inline-flex items-center gap-2 self-start px-3 py-1.5 rounded-full bg-yellow-400/15 border border-yellow-400/30">
                  <Clock className="h-3 w-3 text-yellow-400" />
                  <span className="text-xs font-semibold text-yellow-300 tracking-wide">
                    {normalizedCurrentPlan === 'discover' ? 'Search in progress' : 'Parallel limit reached'}
                  </span>
                </div>
                <h2 className="text-lg font-bold text-white leading-snug">
                  {normalizedCurrentPlan === 'discover'
                    ? 'Get more daily searches'
                    : 'Run 2 searches at the same time'}
                </h2>
                <p className="text-sm text-white/55 leading-relaxed">
                  {normalizedCurrentPlan === 'discover'
                    ? 'Paid plans unlock more searches per day and more results per search. Scale lets you run 2 at once.'
                    : 'Only Scale allows 2 concurrent searches, so you can start a new one without waiting for the current to finish.'}
                </p>
                <div className="mt-auto flex flex-col gap-2 pt-4 border-t border-white/10">
                  {['30-day refund', 'Cancel anytime', 'Instant access'].map((t) => (
                    <div key={t} className="flex items-center gap-2 text-xs text-white/40 font-medium">
                      <div className="w-3.5 h-3.5 rounded-full border border-white/20 flex items-center justify-center flex-shrink-0">
                        <div className="w-1.5 h-1.5 rounded-full bg-white/40" />
                      </div>
                      {t}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: plan selection */}
            <div className="flex flex-col p-6 gap-4">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-text-primary">Choose a plan</h3>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Close modal"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {loading ? (
                <div className="flex justify-center py-10">
                  <div className="animate-spin h-7 w-7 border-4 border-primary border-t-transparent rounded-full" />
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  {filteredPlans.map((plan) => {
                    const isSelected = (selectedPlan?.id ?? recommendedPlan?.id) === plan.id;
                    const isRecommended = recommendedPlan?.id === plan.id;
                    return (
                      <div
                        key={plan.id}
                        onClick={() => setSelectedPlanId(plan.id)}
                        className={`flex items-center gap-4 rounded-xl border p-3.5 cursor-pointer transition-all ${
                          isSelected
                            ? 'border-primary bg-primary/5 shadow-sm ring-2 ring-primary/10'
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                        }`}
                      >
                        {/* Radio */}
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                          isSelected ? 'border-primary' : 'border-gray-300'
                        }`}>
                          {isSelected && <div className="w-2 h-2 rounded-full bg-primary" />}
                        </div>

                        {/* Plan info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-sm font-semibold text-text-primary">{plan.display_name}</span>
                            {isRecommended && (
                              <span className="text-xs font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                                Recommended
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-xs text-text-secondary">
                              {plan.daily_searches} {plan.daily_searches === 1 ? 'search' : 'searches'}/day
                              {plan.max_results != null ? ` · up to ${plan.max_results.toLocaleString()} results/search` : ''}
                            </p>
                            {(plan.features?.max_queued_searches ?? 1) > 1 && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: 'rgba(216,178,110,0.15)', border: '1px solid rgba(216,178,110,0.4)', color: '#B8872A' }}>
                                ⚡ {plan.features.max_queued_searches} parallel
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Price */}
                        <div className="text-right flex-shrink-0">
                          <span className="text-base font-bold text-text-primary">{plan.price_display}</span>
                          <span className="text-xs text-text-secondary">/mo</span>
                        </div>
                      </div>
                    );
                  })}

                  {filteredPlans.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-6">No plans available right now.</p>
                  )}
                </div>
              )}

              {!loading && selectedPlan && (
                <button
                  onClick={() => handlePlanUpgrade(selectedPlan)}
                  disabled={redirecting}
                  className="w-full py-3 px-6 bg-primary hover:bg-primary-hover text-white font-semibold rounded-xl transition-all shadow-md hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {redirecting ? 'Redirecting...' : `Upgrade to ${selectedPlan.display_name}`}
                </button>
              )}
            </div>
          </div>
        </div>

        {redirecting && renderRedirectingOverlay()}
      </div>
    );
  }

  if (activeVariant) {
    const displayPlans = filteredPlans.length > 0 ? filteredPlans : plans.filter((plan) => plan.name !== 'discover' && plan.name !== 'free');
    const selectedWebsiteMatchPlan =
      selectedPlan ?? recommendedPlan ?? displayPlans.find((plan) => plan.name === 'starter') ?? displayPlans[0];

    if (showAllSmartFilterPlans) {
      return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
              <button
                onClick={() => setShowAllSmartFilterPlans(false)}
                className="text-sm font-medium text-[#8A8F9E] hover:text-[#5C6478] transition-colors"
              >
                ← Back
              </button>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors" aria-label="Close modal">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6">
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {displayPlans.map((plan) => {
                    const isRecommended = recommendedPlan?.id === plan.id;
                    const maxSearches = plan.features?.max_queued_searches || 1;
                    return (
                      <div
                        key={plan.id}
                        className={`relative rounded-xl transition-all flex flex-col h-full ${
                          isRecommended
                            ? 'border-2 border-primary shadow-xl ring-2 ring-primary/20'
                            : 'border-2 border-gray-200 hover:border-primary hover:shadow-lg'
                        }`}
                      >
                        <div className="relative bg-white rounded-[8px] p-6 flex flex-col h-full">
                          <div className="mb-3 flex flex-col gap-2">
                            {isRecommended && (
                              <div className="inline-flex items-center bg-primary text-white text-xs font-semibold px-3 py-1 rounded-full self-start">
                                Recommended
                              </div>
                            )}
                          </div>
                          <div className="flex-1 flex flex-col">
                            <h3 className="text-2xl font-bold text-primary mb-3">{plan.display_name}</h3>
                            <div className="flex items-baseline gap-2 mb-3">
                              <span className="text-4xl font-bold text-secondary">{plan.price_display}</span>
                              <span className="text-gray-600 text-lg">per month</span>
                            </div>
                            <div className="space-y-2 mb-5">
                              <p className="text-sm text-gray-700 font-semibold">
                                {plan.daily_searches} {plan.daily_searches === 1 ? 'search' : 'searches'} / day
                              </p>
                              {plan.max_results != null && (
                                <p className="text-sm text-gray-700 font-semibold">
                                  Up to {plan.max_results.toLocaleString()} results / search
                                </p>
                              )}
                              <p className="text-sm text-gray-700">{formatActiveSearches(maxSearches)}</p>
                              <p className="text-sm text-gray-700">
                                {plan.monthly_dm_unlocks.toLocaleString()} people enrichments / month
                              </p>
                              <p className="text-sm text-gray-700">
                                {plan.monthly_email_verifications.toLocaleString()} verifications / month
                              </p>
                            </div>
                            <button
                              onClick={() => void handlePlanUpgrade(plan)}
                              className={`mt-auto py-4 px-8 rounded-lg font-bold text-lg transition-all whitespace-nowrap shadow-md hover:shadow-xl ${
                                isRecommended
                                  ? 'bg-primary hover:bg-primary-hover text-white'
                                  : 'bg-secondary hover:bg-secondary-hover text-white'
                              }`}
                            >
                              Upgrade Now
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          {redirecting && renderRedirectingOverlay()}
        </div>
      );
    }

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
        <div className="relative max-h-[90vh] w-full max-w-4xl overflow-y-auto overflow-x-hidden rounded-2xl bg-white shadow-xl">
          <button
            onClick={onClose}
            className="absolute right-6 top-6 z-20 text-[#8A8F9E] transition-colors hover:text-[#5C6478]"
            aria-label="Close modal"
          >
            <X className="h-6 w-6" />
          </button>

          <div className="grid md:grid-cols-[1.05fr_0.95fr]">
            <div className="bg-[radial-gradient(circle_at_top_left,_rgba(216,178,110,0.18),_transparent_45%),linear-gradient(180deg,_#FBF7F0_0%,_#F8F2EA_100%)] px-6 py-7 sm:px-8">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#E5D2B5] bg-white px-4 py-2 shadow-sm">
                <SlidersHorizontal className="h-3.5 w-3.5 text-[#B3261E]" />
                <span className="text-xs font-semibold uppercase tracking-[0.12em] text-[#B3261E]">{activeVariant.badge}</span>
              </div>

              <h2 className="max-w-xl text-3xl font-bold leading-tight text-[#14213D] sm:text-[36px]">
                {activeVariant.title}
              </h2>

              <div className="mt-6 rounded-2xl border border-[#E7D7C3] bg-white px-5 py-5 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#B3261E]">{activeVariant.example.label}</p>
                <h3 className="mt-2 text-lg font-bold leading-tight text-[#18233F]">
                  {WEBSITE_MATCH_EXAMPLE.businessType}
                </h3>

                <div className="mt-4">
                  <p className="mb-2 text-xs font-medium text-[#8A8F9E]">Match filters</p>
                  <div className="flex flex-wrap gap-2">
                    {WEBSITE_MATCH_EXAMPLE.filters.map((tag) => (
                      <span key={tag} className="rounded-full bg-[#F5ECE8] px-3 py-1 text-xs font-medium text-[#5C3D2E]">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mt-4 border-t border-[#F0E8DF] pt-4">
                  <p className="mb-2 text-xs font-medium text-[#8A8F9E]">Site match</p>
                  <div className="space-y-1.5">
                    {WEBSITE_MATCH_EXAMPLE.matches.map((match) => (
                      <div key={match} className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 shrink-0 text-[#B3261E]" />
                        <span className="text-sm text-[#24324D]">{match}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-6 divide-y divide-[#E9DED0] bg-transparent">
                {activeVariant.featureItems.map((item, idx) => (
                  <div key={item.title} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#E5D2B5] bg-white">
                      {idx === 0 ? (
                        <Target className="h-4 w-4 text-[#B3261E]" />
                      ) : (
                        <Mail className="h-4 w-4 text-[#B3261E]" />
                      )}
                    </div>
                    <p className="text-sm leading-6 text-[#24324D]">{item.title}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col justify-center bg-white px-6 py-7 pr-14 sm:px-8 sm:pr-14">
              <p className="text-sm font-semibold uppercase tracking-[0.12em] text-[#B3261E]">{activeVariant.rightTitle}</p>

              {loading ? (
                <div className="flex flex-1 items-center justify-center py-10">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                </div>
              ) : selectedWebsiteMatchPlan ? (
                <>
                  <div className="mt-5 rounded-2xl border-2 border-[#D8A43C] bg-[#FFFEFB] p-5 shadow-sm">
                    <div className="inline-flex rounded-full bg-[#D8A43C] px-4 py-1.5 text-xs font-semibold text-white">
                      Recommended
                    </div>
                    <div className="mt-5 flex items-end justify-between gap-4">
                      <div className="min-w-0">
                        <h3 className="text-3xl font-bold leading-none text-[#14213D]">
                          {selectedWebsiteMatchPlan.display_name}
                        </h3>
                        <p className="mt-2 max-w-[12rem] text-sm leading-6 text-[#5C6478]">{activeVariant.includedLabel}</p>
                      </div>
                      <div className="shrink-0 text-right">
                        <span className="text-4xl font-bold leading-none text-[#14213D]">{selectedWebsiteMatchPlan.price_display}</span>
                        <span className="text-lg text-[#7A8191]">/mo</span>
                      </div>
                    </div>

                    <div className="mt-5 space-y-3 border-t border-[#E9DED0] pt-5">
                      <div className="flex items-center gap-2.5">
                        <Search className="h-4 w-4 shrink-0 text-indigo-500" />
                        <p className="text-sm font-medium text-[#24324D]">
                          {formatSearchesPerDay(selectedWebsiteMatchPlan.daily_searches)}
                        </p>
                      </div>
                      {selectedWebsiteMatchPlan.max_results != null && (
                        <div className="flex items-center gap-2.5">
                          <CheckCircle2 className="h-4 w-4 shrink-0 text-[#D8A43C]" />
                          <p className="text-sm font-medium text-[#24324D]">
                            {selectedWebsiteMatchPlan.max_results.toLocaleString()} results / search
                          </p>
                        </div>
                      )}
                      <div className="flex items-center gap-2.5">
                        <UserCheck className="h-4 w-4 shrink-0 text-purple-500" />
                        <p className="text-sm font-medium text-[#24324D]">
                          {selectedWebsiteMatchPlan.monthly_dm_unlocks.toLocaleString()} people enrichments / month
                        </p>
                      </div>
                      <div className="flex items-center gap-2.5">
                        <SlidersHorizontal className="h-4 w-4 shrink-0 text-[#B3261E]" />
                        <p className="text-sm font-medium text-[#B3261E]">{activeVariant.includedFeatureLabel}</p>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => handlePlanUpgrade(selectedWebsiteMatchPlan)}
                    disabled={redirecting}
                    className="mt-6 w-full rounded-xl bg-[#121C33] px-6 py-4 text-base font-semibold text-white transition-colors hover:bg-[#0B1427] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {redirecting
                      ? 'Redirecting...'
                      : `Find the right businesses today →`}
                  </button>
                </>
              ) : (
                <div className="flex flex-1 items-center justify-center py-10">
                  <p className="text-sm text-gray-500">No paid plans are available right now.</p>
                </div>
              )}

              <button
                onClick={() => setShowAllSmartFilterPlans(true)}
                className="mt-4 text-center text-sm font-medium text-[#14213D] underline transition-colors hover:text-[#5C6478]"
              >
                Compare all plans
              </button>
              <button
                onClick={onClose}
                className="mt-3 text-center text-sm font-medium text-[#8A8F9E] transition-colors hover:text-[#5C6478]"
              >
                {activeVariant.dismissLabel}
              </button>
            </div>
          </div>
        </div>

        {redirecting && renderRedirectingOverlay()}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-primary">{computedTitle}</h2>
            <p className="text-sm text-gray-600 mt-1">{computedSubtitle}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close modal"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {context === 'locked_page' && (
            <div className="mb-6 bg-gradient-to-r from-orange-50 to-yellow-50 border-l-4 border-orange-400 p-5 rounded-lg">
              <p className="text-gray-700">
                You are trying to access locked results. Upgrade to continue.
              </p>
            </div>
          )}

          {context === 'results_lock' && lockedPlacesCount > 0 && (
            <div className="mb-6 bg-gradient-to-r from-yellow-50 via-orange-50 to-red-50 border-2 border-orange-400 rounded-xl p-4">
              <p className="text-gray-900 font-semibold">
                <span className="text-primary font-bold">{lockedPlacesCount.toLocaleString()} businesses</span> are currently locked.
              </p>
            </div>
          )}

          {!shouldShowPlansOnly && canShowTopups && (
            <div className="flex justify-center mb-6">
              <div className="relative p-[2px] rounded-2xl animate-shimmer-border shadow-lg">
                <div className="relative flex bg-gray-100 p-1.5 rounded-[14px] gap-2 z-10">
                  <button
                    onClick={() => setActiveTab('plans')}
                    className={`flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-base min-w-[200px] transition-all duration-200 ${
                      activeTab === 'plans'
                        ? 'bg-white text-primary font-bold shadow-sm'
                        : 'text-gray-500 hover:text-gray-900 font-medium'
                    }`}
                  >
                    Upgrade Plan
                  </button>
                  <button
                    onClick={() => setActiveTab('topups')}
                    className={`flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-base min-w-[200px] transition-all duration-200 ${
                      activeTab === 'topups'
                        ? 'bg-white text-primary font-bold shadow-sm'
                        : 'text-gray-500 hover:text-gray-900 font-medium'
                    }`}
                  >
                    Buy Top-ups
                  </button>
                </div>
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
            </div>
          ) : shouldRenderTopups ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {topupPacks.map((pack) => (
                <div key={pack.id} className="border-2 border-gray-200 rounded-lg p-4 hover:border-primary transition-colors bg-white">
                  <h3 className="text-lg font-bold text-primary mb-2">{pack.name}</h3>
                  <div className="text-2xl font-bold text-secondary mb-1">{pack.price_display}</div>
                  <button
                    onClick={() => handleTopupPurchase(pack.id)}
                    className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white py-2 px-4 rounded-lg font-semibold transition-colors"
                  >
                    Purchase
                  </button>
                </div>
              ))}
              {topupPacks.length === 0 && (
                <p className="text-sm text-gray-600">No top-up packs are currently available.</p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {filteredPlans.map((plan) => {
                const isRecommended = recommendedPlan?.id === plan.id;
                const maxSearches = plan.features?.max_queued_searches || 1;

                return (
                  <div
                    key={plan.id}
                    className={`relative rounded-xl transition-all flex flex-col h-full ${
                      isRecommended
                        ? 'border-2 border-primary shadow-xl ring-2 ring-primary/20'
                        : 'border-2 border-gray-200 hover:border-primary hover:shadow-lg'
                    }`}
                  >
                    <div className="relative bg-white rounded-[8px] p-6 flex flex-col h-full">
                      <div className="mb-3 flex flex-col gap-2">
                        {isRecommended && (
                          <div className="inline-flex items-center bg-primary text-white text-xs font-semibold px-3 py-1 rounded-full self-start">
                            Recommended
                          </div>
                        )}
                      </div>

                      <div className="flex-1 flex flex-col">
                        <h3 className="text-2xl font-bold text-primary mb-3">{plan.display_name}</h3>
                        <div className="flex items-baseline gap-2 mb-3">
                          <span className="text-4xl font-bold text-secondary">{plan.price_display}</span>
                          <span className="text-gray-600 text-lg">per month</span>
                        </div>

                        <div className="space-y-2 mb-5">
                          <p className="text-sm text-gray-700 font-semibold">
                            {plan.daily_searches} {plan.daily_searches === 1 ? 'search' : 'searches'} / day
                          </p>
                          {plan.max_results != null && (
                            <p className="text-sm text-gray-700 font-semibold">
                              Up to {plan.max_results.toLocaleString()} results / search
                            </p>
                          )}
                          <p className="text-sm text-gray-700">{formatActiveSearches(maxSearches)}</p>
                          <p className="text-sm text-gray-700">
                            {plan.monthly_dm_unlocks.toLocaleString()} people enrichments / month
                          </p>
                          <p className="text-sm text-gray-700">
                            {plan.monthly_email_verifications.toLocaleString()} verifications / month
                          </p>
                        </div>

                        <button
                          onClick={() => handlePlanUpgrade(plan)}
                          className={`mt-auto py-4 px-8 rounded-lg font-bold text-lg transition-all whitespace-nowrap shadow-md hover:shadow-xl ${
                            isRecommended
                              ? 'bg-primary hover:bg-primary-hover text-white'
                              : 'bg-secondary hover:bg-secondary-hover text-white'
                          }`}
                        >
                          Upgrade Now
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {!loading && !shouldRenderTopups && filteredPlans.length === 0 && (
            <div className="text-center py-10">
              <p className="text-gray-700 font-medium">No higher plans are available for this limit right now.</p>
            </div>
          )}
        </div>
      </div>

      {redirecting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white bg-opacity-95">
          <div className="text-center">
            <div className="inline-block animate-bounce text-6xl mb-4">🚀</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Redirecting to checkout...</h3>
            <p className="text-gray-600">Please wait while we prepare your payment</p>
          </div>
        </div>
      )}
    </div>
  );
};
